import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { ColumnDef } from '@tanstack/react-table';
import {
  Filter,
  ChevronDown,
  Calendar,
  User,
  CreditCard,
  Globe,
  Award
} from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { fetchCustomerDetails, CustomerDetailResponse } from '../../services/customer';
import CustomerDetailModal from './CustomerDetailModal';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import { Customer, CustomerFilters } from '../../services/customer';
import { getPartnersList } from '../../services/merchant'
import { UserRole } from '../../types/auth';
import {
  fetchCustomersThunk,
  setFilters,
  resetFilters,
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
} from '../../features/customers/customerSlice';
import { validateCustomerSearchInput } from "../../utils/searchValidator";


const ManageCustomers: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector(state => state.auth);
  const {
    customers,
    loading,
    page,
    pageSize,
    total,
    sortField,
    sortOrder,
    searchQuery,
    filters,
    hasFetched,
  } = useAppSelector(state => state.customers);

  // Table state


  // Customer detail modal state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerDetailOpen, setCustomerDetailOpen] = useState(false);
  const [customerDetailData, setCustomerDetailData] = useState<CustomerDetailResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const isCleverUser = user?.role === UserRole.CLEVERPE_USER;
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };



  const hasSearchOrFilters =
    searchQuery.trim() !== '' ||
    !!filters.partner ||
    !!filters.dateRange?.from ||
    !!filters.dateRange?.to


  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const categories = Array.isArray(user?.groups) ? user.groups : [];

        const partners = await getPartnersList(categories);
        setAvailableGroups(partners);
      } catch (error) {
        console.error('Error fetching partner groups:', error);
      }
    };

    fetchGroups();
  }, [user]);


  // Filter state

  const [showFilters, setShowFilters] = useState(false);

  // Track last fetched query
  const prevRef = useRef({
    page,
    pageSize,
    sortField,
    sortOrder,
    searchQuery,
    filters
  });

  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortField !== sortField ||
      prev.sortOrder !== sortOrder ||
      prev.filters.platform !== filters.platform ||
      prev.filters.partner !== filters.partner ||
      prev.filters.dateRange?.from !== filters.dateRange?.from ||
      prev.filters.dateRange?.to !== filters.dateRange?.to;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchCustomersThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        filters: {
          ...filters,
          dateRange: { ...filters.dateRange },
        },
        page,
        pageSize,
        sortField,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    filters.platform,
    filters.partner,
    filters.dateRange?.from,
    filters.dateRange?.to,
    page,
    pageSize,
    sortField,
    sortOrder,
    hasFetched,
    dispatch,
  ]);

  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;

    if (!isCleverUser || hasSearchOrFilters) {
      dispatch(fetchCustomersThunk());
    }
  }, [token, hasFetched, isCleverUser, hasSearchOrFilters, dispatch]);


  // Handle row click to view customer details
  const handleRowClick = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerDetailOpen(true);
    setIsLoadingDetail(true);
    try {
      const data = await fetchCustomerDetails(customer.mobileNo, token);
      setCustomerDetailData(data);
    } catch (err) {
      console.error('Error fetching customer details:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Close modal
  const handleCloseCustomerDetail = () => {
    setCustomerDetailOpen(false);
    setSelectedCustomer(null);
    setCustomerDetailData(null);
  };


  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format platform for display
  const formatPlatform = (platform: string | null) => {
    if (!platform) return "N/A";

    switch (platform.toLowerCase()) {
      case 'merchant':
        return 'Merchant';
      case 'gateway-partner':
      case 'gateway':
        return 'Gateway';
      case 'customer':
        return 'Website';
      default:
        return platform;
    }
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender: string | null) => {
    if (!lender) return <span className="text-xs text-gray-500">N/A</span>;

    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'general':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  // Table columns
  const columns: ColumnDef<Customer>[] = [
    {
      id: 'serialNo',
      header: 'Sr. No',
      cell: ({ row }) => {
        // Use the serialNumber property from the customer object if available,
        // otherwise fall back to calculating it
        const serialNo = row.original.serialNumber || ((page - 1) * pageSize + row.index + 1);
        return (
          <span className="text-xs font-medium">
            {serialNo}
          </span>
        );
      },
      size: 50 // Make column narrower
    },
    {
      id: 'customerDetails',
      header: 'Customer Details',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <div className="flex items-center">
            <User size={14} className="flex-shrink-0 mr-1 text-blue-500" />
            <span className="text-sm font-medium" title={row.original.name || "N/A"}>
              {row.original.name || "N/A"}
            </span>
          </div>
          <div className="flex items-center mt-1 text-xs text-gray-500">
            <span className="ml-5">{row.original.mobileNo}</span>
          </div>
        </div>
      ),
      size: 180
    },
    {
      id: 'pan',
      header: 'PAN No',
      accessorKey: 'pan',
      cell: ({ row }) => (
        <div className="flex items-center">
          <CreditCard size={14} className="flex-shrink-0 mr-1 text-gray-500" />
          <span className="text-sm">
            {row.original.pan || "N/A"}
          </span>
        </div>
      ),
      size: 120
    },
    {
      id: 'platform',
      header: 'Platform',
      accessorKey: 'platform',
      cell: ({ row }) => (
        <div className="flex items-center">
          <Globe size={14} className="flex-shrink-0 mr-1 text-green-500" />
          <span className="text-sm">
            {formatPlatform(row.original.platform)}
          </span>
        </div>
      ),
      size: 120
    },
    // {
    //   id: 'matchLender',
    //   header: 'Match Lender',
    //   accessorKey: 'preApprovedMaxMatchLender',
    //   cell: ({ row }) => (
    //     <div className="flex items-center">
    //       {renderLenderBadge(row.original.preApprovedMaxMatchLender)}
    //     </div>
    //   ),
    //   size: 120
    // },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <div className="flex items-center">
          <Calendar size={14} className="flex-shrink-0 mr-1 text-gray-500" />
          <span className="text-sm">
            {formatDate(row.original.createdAt)}
          </span>
        </div>
      ),
      size: 180
    },
  ];

  // Handle filter changes
  const handleFilterChange = (
    key: keyof CustomerFilters,
    value: any
  ) => {
    dispatch(
      setFilters({
        ...filters, // keep existing filters
        [key]:
          key === "dateRange"
            ? { ...filters.dateRange, ...value }
            : value,
      })
    );
  };

  const handleRefresh = () => {
    // 🔹 Clear local search UI state
    setLocalSearch("");
    setSearchError(null);
    setIsSearchValid(false);

    // 🔹 Clear redux search
    dispatch(setSearchQuery(""));

    // 🔹 Reset pagination
    dispatch(setPage(1));

    // 🔹 Fetch fresh data
    dispatch(fetchCustomersThunk());
  };



  const handleResetFilters = () => {
    dispatch(resetFilters());
  };



  const toggleFilters = () => setShowFilters(prev => !prev);

  return (
    <div className="w-full no-scrollbar">
      {/* Filters and search section */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          onClick={toggleFilters}
          className="flex items-center"
          size="sm"
        >
          <Filter size={14} className="mr-1" />
          Filters
          <ChevronDown size={14} className="ml-1" />
        </Button>
      </div>

      {/* Filter controls */}
      {showFilters && (
        <div className="p-4 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm no-scrollbar">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {isCleverUser ? (
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Partner/Group
                </label>
                <Select
                  value={filters.partner ? { value: filters.partner, label: filters.partner } : null}
                  onChange={(option) => handleFilterChange('partner', option ? option.value : '')}
                  options={availableGroups.map(group => ({ value: group, label: group }))}
                  isClearable
                  placeholder="Search or select partner..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                      minHeight: '42px',
                      boxShadow: 'none',
                    }),
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: '#9CA3AF',
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isSelected
                        ? '#E0E7FF'
                        : state.isFocused
                          ? '#F3F4F6'
                          : 'transparent',
                      color: state.isSelected ? '#4F46E5' : '#374151',
                      ':active': {
                        backgroundColor: '#E0E7FF',
                      },
                    }),
                  }}
                />
              </div>
            ) : (
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Platform
                </label>
                <select
                  value={filters?.platform}
                  onChange={(e) => handleFilterChange('platform', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Platforms</option>
                  <option value="gateway-partner">Gateway</option>
                  <option value="merchant">Merchant</option>
                  <option value="customer">Website</option>
                </select>
              </div>
            )}


            {/* Date range filter */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters?.dateRange?.from || ''}
                  onChange={(e) => handleFilterChange('dateRange', { from: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters?.dateRange?.to || ''}
                  onChange={(e) => handleFilterChange('dateRange', { to: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Filter actions */}
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={handleResetFilters}
              size="sm"
              className="mr-2"
            >
              Reset
            </Button>
            <Button
              onClick={() => {
                dispatch(fetchCustomersThunk());
              }}
              size="sm"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={
            !isCleverUser || hasSearchOrFilters
              ? customers // ✅ Show data
              : []        // ❌ Hide unless filters/search
          }
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}

          /* Pagination */
          onPageChange={(newpage) => dispatch(setPage(newpage))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          // onSearchChange={(value) => {
          //   if (value !== searchQuery) {
          //     dispatch(setPage(1));
          //     dispatch(setSearchQuery(value));
          //   }
          // }}
          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new
          onRefresh={handleRefresh}
          searchPlaceholder="Search by name, mobile number..."
          emptyStateMessage={
            isCleverUser && !hasSearchOrFilters
              ? 'Please apply search or filter to view customers.'
              : 'No merchants found.'
          }

          /* Row click */
          enableRowClick={true}
          onRowClick={handleRowClick}
        />

      </div>

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        isOpen={customerDetailOpen}
        onClose={handleCloseCustomerDetail}
        customerData={customerDetailData}
        isLoading={isLoadingDetail}
      />
    </div>
  );
};

export default ManageCustomers; 